import './CustomData.scss';

import type { User } from 'api/models/mainservice';
import type {
  CustomDataObject,
  CustomForm,
  LoggedUserCompany,
  PublicOrderCustomForm,
  RelatedFieldObject,
  ReportTemplate
} from 'common/models';
import LabeledText from 'components/LabeledText/LabeledText';
import dayjs from 'dayjs';
import { compareDayjsDatesDesc } from 'helpers/comparing';
import { getFullName } from 'helpers/contactPersonHelpers';
import { getTotalCost, getTotalCostData } from 'helpers/customDataHelper';
import {
  getCustomDataForReport,
  getMappedReport,
  getReportHidePriceParams
} from 'helpers/reportHelper';
import type { InterventionDetail } from 'pages/interventions/models';
import type { OrderDetail } from 'pages/orders/models';
import type { ServiceObjectInCustomer } from 'pages/serviceObjects/models';
import type { Codebooks } from 'pages/settings/codebooks/models';
import React from 'react';

import CustomDataBlock from './CustomDataBlock';
import CustomDataLabeledText from './CustomDataLabeledText';
import CustomDataReportSection from './CustomDataReportSection';
import CustomDataText from './CustomDataText';
import ServiceObjectData from './ServiceObject/ServiceObjectData';

interface OwnProps {
  customForm: CustomForm | PublicOrderCustomForm;
  reportTemplate: ReportTemplate;
  company?: LoggedUserCompany;
  order?: OrderDetail;
  intervention?: InterventionDetail;
  assignee?: User;
  codebooks?: Codebooks;
  serviceObjectId?: string;
  users?: User[];
  isPublic?: boolean;
}

const CustomDataReport = React.forwardRef<HTMLDivElement, OwnProps>((props, ref) => {
  const {
    customForm,
    company,
    order,
    intervention,
    assignee,
    codebooks,
    reportTemplate,
    serviceObjectId,
    users,
    isPublic
  } = props;

  const object: RelatedFieldObject = customForm.relatedData
    ? {
        ...customForm.relatedData,
        order: customForm.relatedData.order && {
          ...customForm.relatedData.order,
          customData:
            customForm.relatedData.order.customData &&
            (getCustomDataForReport(customForm.relatedData.order.customData) as any),
          serviceObjects: customForm.relatedData.order.serviceObjects.map((serviceObject) => {
            if (serviceObject.customData && Object.keys(serviceObject.customData).length !== 0) {
              return {
                ...serviceObject,
                customData: getCustomDataForReport(serviceObject.customData) as any
              };
            }

            return serviceObject;
          })
        },
        customer: customForm.relatedData.customer && {
          ...customForm.relatedData.customer,
          customData:
            customForm.relatedData.customer.customData &&
            (getCustomDataForReport(customForm.relatedData.customer.customData) as any)
        }
      }
    : {
        company: company && { ...company },
        order: order && {
          ...order,
          customData: order.customData && (getCustomDataForReport(order.customData) as any),
          serviceObjects: order.serviceObjects.map((serviceObject) => {
            if (serviceObject.customData && Object.keys(serviceObject.customData).length !== 0) {
              return {
                ...serviceObject,
                customData: getCustomDataForReport(serviceObject.customData) as any
              };
            }

            return serviceObject;
          })
        },
        customer: order && {
          ...order.customer,
          customData:
            order.customer.customData && (getCustomDataForReport(order.customer.customData) as any)
        },
        invoiceCustomer: order?.invoiceCustomer,
        primaryAssignee: assignee && { ...assignee },
        intervention: intervention && {
          ...intervention,
          assignations: intervention.assignations.map((assignee) => {
            const user = users?.find((x) => x.id === assignee.userId);

            return {
              ...assignee,
              fullName: user ? getFullName(user) : ''
            };
          })
        },
        codebooks: codebooks && { ...codebooks }
      };

  const getMappedData = (data: CustomDataObject) =>
    getMappedReport(
      reportTemplate,
      customForm.template.form.sections,
      data,
      object,
      customForm.serviceObjectIds,
      serviceObjectId
    );

  const data = customForm.data ? getMappedData(customForm.data) : [];

  const totalCostData = customForm.data && getTotalCostData(customForm.data);
  let totalCost = 0;

  const customFormServiceObjects = customForm.serviceObjectIds.reduce(
    (acc: ServiceObjectInCustomer[], serviceObjectId: string) => {
      const serviceObject = object.order?.serviceObjects.find((x) => x.id === serviceObjectId);

      if (serviceObject) {
        return [...acc, serviceObject];
      }

      return acc;
    },
    []
  );

  if (totalCostData) {
    const pricePerKm = object?.company?.pricePerKilometer;
    const companyTravelFee = object?.company?.travelFee;
    const addressTravelFee = object?.order!.address.travelFee;
    const travelFee = addressTravelFee || companyTravelFee || 0;

    totalCost = getTotalCost(
      totalCostData,
      getReportHidePriceParams(reportTemplate.content),
      object?.order?.address.hasFlatRateTravelFee,
      travelFee,
      pricePerKm
    );
  }

  const serviceObject = object.order?.serviceObjects.find((x) => x.id === serviceObjectId);

  const sortedSignatures = customForm.signatures.sort((a, b) =>
    compareDayjsDatesDesc(dayjs(a.created), dayjs(b.created))
  );

  const signature =
    serviceObjectId && reportTemplate.perServiceObject
      ? sortedSignatures.find(
          (x) => x.reportId === reportTemplate.id && x.serviceObjectId === serviceObjectId
        )
      : sortedSignatures.find((x) => x.reportId === reportTemplate.id);

  return (
    <div className="CustomData Report" ref={ref}>
      {data.map((item, index) => {
        if (item.type === 'FormField') {
          return (
            <CustomDataLabeledText
              customData={item.fieldData!}
              key={item.fieldData?.id}
              totalCost={totalCost}
              currencyId={object.company?.currencyId || ''}
            />
          );
        }

        if (item.type === 'Section') {
          return (
            <CustomDataReportSection
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              section={item}
              object={object}
              serviceObject={serviceObject}
              signature={signature}
              customFormId={customForm.id}
              reportId={reportTemplate.id}
              perServiceObject={reportTemplate.perServiceObject}
              reportTitle={
                serviceObject
                  ? `${reportTemplate.name} - ${serviceObject.name}`
                  : reportTemplate.name
              }
              totalCost={totalCost}
              serviceObjects={customFormServiceObjects}
              isPublic={isPublic}
              customFormCodeNumber={customForm.codeNumber}
            />
          );
        }

        if (item.type === 'CustomBlock') {
          return (
            <CustomDataBlock
              // eslint-disable-next-line react/no-array-index-key
              key={`${item.blockProps?.blockType}-${index}`}
              blockProps={item.blockProps!}
              blockData={item.blockData}
              object={object}
              signature={signature}
              serviceObjectId={serviceObjectId}
              customFormId={customForm.id}
              reportId={reportTemplate.id}
              perServiceObject={reportTemplate.perServiceObject}
              reportTitle={
                serviceObject
                  ? `${reportTemplate.name} - ${serviceObject.name}`
                  : reportTemplate.name
              }
              checkListProps={item.checkListProps}
              totalCost={totalCost}
              serviceObjects={customFormServiceObjects}
              isPublic={isPublic}
              customFormCodeNumber={customForm.codeNumber}
            />
          );
        }

        if (item.type === 'TextField') {
          return <CustomDataText key={item.textValue} text={item.textValue!} />;
        }

        if (item.type === 'ServiceObjectSection') {
          const filteredServiceObjects = item.serviceObjectCategories
            ? customFormServiceObjects.filter((x) =>
                item.serviceObjectCategories?.includes(x.categorySlug)
              )
            : customFormServiceObjects || [];

          if (item.serviceObjectCategories && filteredServiceObjects.length === 0) {
            return (
              <div // eslint-disable-next-line react/no-array-index-key
                key={index}
              />
            );
          }

          return (
            <ServiceObjectData
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              data={item.serviceObjectData!}
              title={item.title}
              displayMode={item.displayMode!}
              serviceObjects={filteredServiceObjects}
              columns={item.columns}
              width={item.width}
            />
          );
        }

        if (item.type === 'Empty') {
          // eslint-disable-next-line react/no-array-index-key
          return <div key={`${item.type}-${index}`} className="Empty" />;
        }

        return (
          <LabeledText
            label={item.properties?.label || ''}
            value={item.textValue || ''}
            key={item.properties?.template}
          />
        );
      })}
    </div>
  );
});

export default CustomDataReport;
